export type AnchoredMenuPlacement = 'top' | 'bottom';

export function placeAnchoredMenu(input: {
  trigger: { top: number; right: number; bottom: number; left: number };
  menuWidth: number;
  menuHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  align: 'start' | 'end';
  gap?: number;
  margin?: number;
  /**
   * `auto` flips above the trigger when the whole menu does not fit below
   * and the space above is larger. `bottom` stays under the trigger while
   * `minHeight` still fits, and flips only when that slice does not.
   */
  prefer?: 'auto' | 'bottom';
  minHeight?: number;
}): { top: number; left: number; maxHeight: number; placement: AnchoredMenuPlacement } {
  const gap = input.gap ?? 8;
  const margin = input.margin ?? 8;
  const { trigger } = input;
  const menuHeight = Math.max(0, input.menuHeight);
  const spaceBelow = Math.max(0, input.viewportHeight - trigger.bottom - gap - margin);
  const spaceAbove = Math.max(0, trigger.top - gap - margin);
  const minSlice =
    input.prefer === 'bottom'
      ? Math.min(menuHeight, Math.max(0, input.minHeight ?? menuHeight))
      : menuHeight;
  const placement: AnchoredMenuPlacement =
    spaceBelow >= minSlice || spaceBelow >= spaceAbove ? 'bottom' : 'top';
  const available = placement === 'bottom' ? spaceBelow : spaceAbove;
  const height = Math.min(menuHeight, available);
  const top =
    placement === 'bottom' ? trigger.bottom + gap : Math.max(margin, trigger.top - gap - height);
  const preferredLeft = input.align === 'end' ? trigger.right - input.menuWidth : trigger.left;
  const maxLeft = Math.max(margin, input.viewportWidth - input.menuWidth - margin);
  const left = Math.min(Math.max(preferredLeft, margin), maxLeft);
  return { top, left, maxHeight: available, placement };
}
